import moment from "moment";
import React, { FC, useEffect } from "react";
import { LuX } from "react-icons/lu";
import { MdOutlineEdit } from "react-icons/md";

import { Avatar } from "../../../components/newUI/avatar";
import { Button } from "../../../components/newUI/button";
import { renderRichText } from "../utils/parseNote";
import { noteHeading } from "./notes/noteHeading";

export type NoteViewModalData = {
  id: string;
  title?: string;
  note?: string;
  createdAt?: Date | string;
  authorName: string;
  authorColor?: string;
  workId?: string;
  workName?: string;
};

type Props = {
  note: NoteViewModalData;
  onClose: () => void;
  onEdit?: () => void;
};

export const NoteViewModal: FC<Props> = ({ note, onClose, onEdit }) => {
  // Esc closes the window like the × (WITEM-046).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className={"modal modal-open !z-[64]"} onClick={onClose}>
      <div
        className={
          "modal-box flex max-h-[calc(100vh-5em)] w-[520px] max-w-full flex-col overflow-hidden rounded-2xl bg-white p-0 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }
        onClick={(event) => event.stopPropagation()}>
        <div
          className={
            "flex shrink-0 items-start justify-between gap-4 border-b border-[#F0F1F4] px-[26px] pb-4 pt-6"
          }>
          <div
            className={
              "min-w-0 text-[19px] font-extrabold leading-[1.3] text-[#1F2433] [overflow-wrap:anywhere]"
            }>
            {noteHeading(note) || "Note"}
          </div>
          <Button
            type={"button"}
            variant={"ghost"}
            size={"sm"}
            iconOnly
            autoWidth
            leftIcon={<LuX size={16} strokeWidth={2.2} />}
            className={
              "h-[30px] w-[30px] rounded-lg p-0 text-[#9097A8] hover:bg-[#F1F2F5]"
            }
            onClick={onClose}
          />
        </div>

        <div
          className={
            "rich-text-content min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-[26px] py-5 text-[15px] leading-[1.65] text-[#3A4256] [overflow-wrap:anywhere] [&_*]:max-w-full [&_img]:h-auto [&_pre]:whitespace-pre-wrap [&_td]:break-words [&_th]:break-words"
          }>
          {renderRichText(note.note || "")}
        </div>

        <div
          className={
            "flex shrink-0 items-center gap-[11px] border-t border-[#F0F1F4] px-[26px] py-4"
          }>
          <Avatar
            id={note.id}
            fullName={note.authorName}
            size={"md"}
            color={note.authorColor}
            avatarClassNames={"h-[34px] w-[34px] text-[11px] font-bold"}
          />
          <span className={"flex min-w-0 flex-col"}>
            <span className={"truncate text-[13.5px] font-bold text-[#1F2433]"}>
              {note.authorName}
            </span>
            <span className={"text-[12.5px] text-[#9097A8]"}>
              {note.createdAt
                ? moment(note.createdAt).format("MMM DD, YYYY")
                : ""}
            </span>
          </span>
          <span className={"flex-1"} />
          {onEdit && (
            <Button
              type={"button"}
              variant={"ghost"}
              size={"sm"}
              autoWidth
              leftIcon={<MdOutlineEdit size={16} />}
              className={
                "h-auto p-0 text-[13.5px] font-bold text-[#6B4EFF] hover:bg-transparent"
              }
              onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
